import React, { useState, useEffect } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import HardcodedLocationSelector from '../component/common/HardcodedLocationSelector';
import { FaSun, FaMoon, FaCalendarAlt, FaSpinner, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Footer from '../component/common/Footer';
import FestivalsSection from '../component/core/Panchang/FestivalsSection';

const Panchang = () => {
  const { t } = useLanguage();
  const [panchangData, setPanchangData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedLocation, setSelectedLocation] = useState({
    name: 'New Delhi',
    address: 'New Delhi, Delhi, India',
    coordinates: '28.6139,77.2090',
    latitude: 28.6139,
    longitude: 77.2090,
    placeId: null
  }); // Default to New Delhi

  const getMockPanchangData = (date) => {
    const selectedDate = new Date(date);
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = dayNames[selectedDate.getDay()];
    
    return {
      panchang: {
        vaara: dayName,
        sunrise: `${date}T06:15:00+05:30`,
        sunset: `${date}T18:30:00+05:30`,
        moonrise: `${date}T20:45:00+05:30`,
        moonset: `${date}T09:20:00+05:30`,
        nakshatra: [
          {
            name: "Rohini",
            lord: "Brahma",
            start: `${date}T06:15:00+05:30`,
            end: `${date}T08:30:00+05:30`
          }
        ],
        tithi: [
          {
            name: "Purnima",
            paksha: "Shukla",
            start: `${date}T06:15:00+05:30`,
            end: `${date}T08:30:00+05:30`
          }
        ],
        karana: [
          {
            name: "Vishti",
            start: `${date}T06:15:00+05:30`,
            end: `${date}T08:30:00+05:30`
          }
        ],
        yoga: [
          {
            name: "Siddhi",
            start: `${date}T06:15:00+05:30`,
            end: `${date}T08:30:00+05:30`
          }
        ],
        auspicious_period: [
          {
            name: "Abhijit Muhurat",
            type: "auspicious",
            start: `${date}T11:45:00+05:30`,
            end: `${date}T12:30:00+05:30`
          }
        ],
        inauspicious_period: [
          {
            name: "Rahu Kaal",
            type: "inauspicious",
            start: `${date}T15:00:00+05:30`,
            end: `${date}T16:30:00+05:30`
          }
        ]
      }
    };
  };

  const fetchPanchangData = React.useCallback(async (date) => {
    try {
      setLoading(true);
      setError(null);

      // Format date for API (YYYY-MM-DDTHH:MM:SS+05:30) - ISO 8601 format with IST timezone
      const dateTime = `${date}T12:00:00+05:30`;
      
      // Get language from localStorage or default to 'en'
      const currentLanguage = localStorage.getItem('language') || 'hi';
      const url = `https://anantaaradhyabackend.onrender.com/astrology/panchang/advanced?ayanamsa=1&coordinates=${selectedLocation.coordinates}&datetime=${encodeURIComponent(dateTime)}&la=${currentLanguage}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.status === 'ok' && data.data) {
        setPanchangData(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch Panchang data');
      }
    } catch (err) {
      console.error('Error fetching Panchang data:', err);
      
      // Set specific error message for debugging
      if (err.message.includes('CORS') || err.message.includes('Network error')) {
        setError(`Connection Error: ${err.message}. Falling back to mock data.`);
      } else if (err.message.includes('Authentication failed')) {
        setError(`Authentication Error: ${err.message}. Please check your API credentials.`);
      } else {
        setError(`API Error: ${err.message}. Falling back to mock data.`);
      }
      
      console.log('Falling back to mock data');
      // Fallback to mock data on error
      setPanchangData(getMockPanchangData(date));
    } finally {
      setLoading(false);
    }
  }, [selectedLocation.coordinates]);

  useEffect(() => {
    fetchPanchangData(selectedDate);
  }, [selectedDate, selectedLocation, fetchPanchangData]);

  // Handle location selection from LocationSearchWithMap
  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
  };

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };

  // Navigation handlers
  const handlePreviousDay = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() - 1);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  const handleNextDay = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + 1);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };



  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return new Date(timeString).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    return new Date(dateTimeString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <FaSpinner className="animate-spin text-4xl text-orange-500 mx-auto mb-4" />
              <p className="text-gray-600">{t('loading')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="text-red-500 text-6xl mb-4">⚠️</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Error Loading Panchang
              </h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={() => fetchPanchangData(selectedDate)}
                className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!panchangData) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                No Panchang Data Available
              </h2>
              <p className="text-gray-600">
                Unable to fetch Panchang data for the selected date.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // panchangData already contains the panchang data directly from backend
  const panchang = panchangData;

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-4">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            {t('panchang')}
          </h1>
          <p className="text-base text-gray-600 mb-4">
            {t('panchangDescription')}
          </p>
          
          {/* Date and Location Selector */}
          <div className="flex flex-col lg:flex-row items-center justify-center gap-4 mb-4">
            {/* Date Navigation and Selector */}
            <div className="flex items-center gap-2">
              {/* Previous Day Button */}
              <button
                onClick={handlePreviousDay}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                title="Previous Day"
              >
                <FaChevronLeft className="text-gray-600" />
              </button>
              
              {/* Date Input */}
              <div className="flex items-center gap-2">
                <FaCalendarAlt className="text-orange-500" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              
              {/* Next Day Button */}
              <button
                onClick={handleNextDay}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                title="Next Day"
              >
                <FaChevronRight className="text-gray-600" />
              </button>
            </div>

            {/* Location Search */}
            <div className="min-w-[300px]">
              <HardcodedLocationSelector
                value={selectedLocation.name}
                onChange={handleLocationSelect}
                placeholder="Search for a city..."
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Basic Information - All 5 cards in one row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          {/* Vaara (Day) */}
          <div className="bg-white rounded-lg shadow-lg p-4 text-center min-h-[120px] flex flex-col justify-center">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('vaara')}</h3>
            <p className="text-xl font-bold text-orange-500">{panchang.vaara}</p>
          </div>

          {/* Sunrise */}
          <div className="bg-white rounded-lg shadow-lg p-4 text-center min-h-[120px] flex flex-col justify-center">
            <div className="flex items-center justify-center mb-2">
              <FaSun className="text-yellow-500 mr-2 text-sm" />
              <h3 className="text-sm font-semibold text-gray-900">{t('sunrise')}</h3>
            </div>
            <p className="text-lg font-bold text-gray-700">{formatTime(panchang.sunrise)}</p>
          </div>

          {/* Sunset */}
          <div className="bg-white rounded-lg shadow-lg p-4 text-center min-h-[120px] flex flex-col justify-center">
            <div className="flex items-center justify-center mb-2">
              <FaSun className="text-orange-500 mr-2 text-sm" />
              <h3 className="text-sm font-semibold text-gray-900">{t('sunset')}</h3>
            </div>
            <p className="text-lg font-bold text-gray-700">{formatTime(panchang.sunset)}</p>
          </div>

          {/* Moonrise */}
          <div className="bg-white rounded-lg shadow-lg p-4 text-center min-h-[120px] flex flex-col justify-center">
            <div className="flex items-center justify-center mb-2">
              <FaMoon className="text-blue-500 mr-2 text-sm" />
              <h3 className="text-sm font-semibold text-gray-900">{t('moonrise')}</h3>
            </div>
            <p className="text-lg font-bold text-gray-700">{formatTime(panchang.moonrise)}</p>
          </div>

          {/* Moonset */}
          <div className="bg-white rounded-lg shadow-lg p-4 text-center min-h-[120px] flex flex-col justify-center">
            <div className="flex items-center justify-center mb-2">
              <FaMoon className="text-indigo-500 mr-2 text-sm" />
              <h3 className="text-sm font-semibold text-gray-900">{t('moonset')}</h3>
            </div>
            <p className="text-lg font-bold text-gray-700">{formatTime(panchang.moonset)}</p>
          </div>
        </div>

        {/* Main Content - Side by Side Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          
          {/* Left Column - Detailed Panchang Information */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('detailedPanchang')}
            </h2>
            
            {/* Panchang Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Nakshatra Card */}
              <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-blue-500">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="w-3 h-3 bg-blue-500 rounded-full mr-3"></span>
                  {t('nakshatra')}
                </h3>
                <div className="space-y-3">
                  {panchang.nakshatra?.map((item, index) => (
                    <div key={index} className="bg-blue-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">{item.name}</h4>
                      <p className="text-sm text-gray-600 mb-1">
                        <strong>{t('lord')}:</strong> {item.lord?.name || item.lord || 'N/A'}
                      </p>
                      <p className="text-sm text-gray-600 mb-1">
                        <strong>{t('start')}:</strong> {formatDateTime(item.start)}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>{t('end')}:</strong> {formatDateTime(item.end)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tithi Card */}
              <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-green-500">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="w-3 h-3 bg-green-500 rounded-full mr-3"></span>
                  {t('tithi')}
                </h3>
                <div className="space-y-3">
                  {panchang.tithi?.map((item, index) => (
                    <div key={index} className="bg-green-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">{item.name}</h4>
                      <p className="text-sm text-gray-600 mb-1">
                        <strong>{t('paksha')}:</strong> {item.paksha}
                      </p>
                      <p className="text-sm text-gray-600 mb-1">
                        <strong>{t('start')}:</strong> {formatDateTime(item.start)}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>{t('end')}:</strong> {formatDateTime(item.end)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Karana Card */}
              <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-purple-500">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="w-3 h-3 bg-purple-500 rounded-full mr-3"></span>
                  {t('karana')}
                </h3>
                <div className="space-y-3">
                  {panchang.karana?.map((item, index) => (
                    <div key={index} className="bg-purple-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">{item.name}</h4>
                      <p className="text-sm text-gray-600 mb-1">
                        <strong>{t('start')}:</strong> {formatDateTime(item.start)}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>{t('end')}:</strong> {formatDateTime(item.end)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Yoga Card */}
              <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-orange-500">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="w-3 h-3 bg-orange-500 rounded-full mr-3"></span>
                  {t('yoga')}
                </h3>
                <div className="space-y-3">
                  {panchang.yoga?.map((item, index) => (
                    <div key={index} className="bg-orange-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">{item.name}</h4>
                      <p className="text-sm text-gray-600 mb-1">
                        <strong>{t('start')}:</strong> {formatDateTime(item.start)}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>{t('end')}:</strong> {formatDateTime(item.end)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Auspicious Period Card */}
              <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-emerald-500">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="w-3 h-3 bg-emerald-500 rounded-full mr-3"></span>
                  Auspicious Periods
                </h3>
                <div className="space-y-3">
                  {panchang.auspicious_period?.map((item, index) => (
                    <div key={index} className="bg-emerald-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">{item.name}</h4>
                      <p className="text-sm text-gray-600 mb-1">
                        <strong>Type:</strong> {item.type}
                      </p>
                      {item.period?.map((period, periodIndex) => (
                        <div key={periodIndex} className="ml-4 mb-2 p-2 bg-white rounded border-l-2 border-emerald-300">
                          <p className="text-sm text-gray-600 mb-1">
                            <strong>{t('start')}:</strong> {formatDateTime(period.start)}
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>{t('end')}:</strong> {formatDateTime(period.end)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* Inauspicious Period Card */}
              <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-red-500">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="w-3 h-3 bg-red-500 rounded-full mr-3"></span>
                  Inauspicious Periods
                </h3>
                <div className="space-y-3">
                  {panchang.inauspicious_period?.map((item, index) => (
                    <div key={index} className="bg-red-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">{item.name}</h4>
                      <p className="text-sm text-gray-600 mb-1">
                        <strong>Type:</strong> {item.type}
                      </p>
                      {item.period?.map((period, periodIndex) => (
                        <div key={periodIndex} className="ml-4 mb-2 p-2 bg-white rounded border-l-2 border-red-300">
                          <p className="text-sm text-gray-600 mb-1">
                            <strong>{t('start')}:</strong> {formatDateTime(period.start)}
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>{t('end')}:</strong> {formatDateTime(period.end)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>

          {/* Right Column - Festivals Section */}
          <div className="space-y-6">
            <FestivalsSection />
          </div>

        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Panchang;
